import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Circle, Loader2, Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { checklistApi } from '@/services/checklist'
import type { JobChecklistItem } from '@/types'

const isItemComplete = (item: JobChecklistItem) => item.status === 'completed' || item.status === 'skipped'

type JobChecklistPanelProps = {
  jobId: string
  workDayId?: string | number
  readOnly?: boolean
  kind?: 'job' | 'project' | 'event'
}

export function JobChecklistPanel({ jobId, workDayId, readOnly = false, kind = 'job' }: JobChecklistPanelProps) {
  const queryClient = useQueryClient()
  const queryKey = ['job-checklist', jobId, workDayId ?? 'job']
  const [newItemTitle, setNewItemTitle] = useState('')

  const checklistQuery = useQuery({
    queryKey,
    queryFn: () => checklistApi.getJobChecklist(jobId, workDayId, kind),
    enabled: Boolean(jobId),
  })

  const toggleMutation = useMutation({
    mutationFn: async ({ item, checked }: { item: JobChecklistItem; checked: boolean }) => {
      if (checked) {
        return checklistApi.completeItem(item.id)
      }
      return checklistApi.reopenItem(item.id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const applyTemplateMutation = useMutation({
    mutationFn: () => checklistApi.applyTemplate(jobId, kind),
    onSuccess: () => {
      toast.success('Checklist template applied')
      queryClient.invalidateQueries({ queryKey })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const addItemMutation = useMutation({
    mutationFn: (title: string) => checklistApi.addJobChecklistItem(jobId, {
      title,
      is_required: true,
      work_day_id: workDayId,
    }, kind),
    onSuccess: () => {
      toast.success('Checklist item added')
      setNewItemTitle('')
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  if (checklistQuery.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  const items = checklistQuery.data?.items ?? []
  const summary = checklistQuery.data?.summary
  const templateAvailable = checklistQuery.data?.template_available
  const progressValue = summary && summary.required_total > 0
    ? Math.round((summary.required_completed / summary.required_total) * 100)
    : 100

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div>
          <p className="text-sm font-medium">
            {workDayId ? 'Daily checklist' : 'Job checklist'}
          </p>
          <p className="text-xs text-muted-foreground">
            {summary?.required_completed ?? 0} of {summary?.required_total ?? 0} required steps complete
          </p>
          {!workDayId ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Steps are copied automatically when a job is created from a matching template.
            </p>
          ) : null}
        </div>
        <Progress value={progressValue} className="h-2" />
      </div>

      {!readOnly ? (
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            const title = newItemTitle.trim()
            if (!title) {
              return
            }
            addItemMutation.mutate(title)
          }}
        >
          <Input
            value={newItemTitle}
            onChange={(event) => setNewItemTitle(event.target.value)}
            placeholder="Add ad-hoc checklist step"
            disabled={addItemMutation.isPending}
          />
          <Button type="submit" variant="outline" disabled={addItemMutation.isPending || !newItemTitle.trim()}>
            {addItemMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </form>
      ) : null}

      {items.length === 0 ? (
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>No checklist steps on this {workDayId ? 'work day' : 'job'} yet.</p>
          {templateAvailable === false ? (
            <p>
              No active template matches this job&apos;s category (and no org default is set).
              Add a template under Templates below, or add steps manually.
            </p>
          ) : (
            <p>
              A template is configured; steps should appear automatically for new jobs.
              If this is an older job, use sync below or add steps manually.
            </p>
          )}
          {!readOnly && templateAvailable !== false ? (
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 text-sm"
              onClick={() => applyTemplateMutation.mutate()}
              disabled={applyTemplateMutation.isPending}
            >
              {applyTemplateMutation.isPending ? 'Syncing from template…' : 'Sync from template'}
            </Button>
          ) : null}
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => {
            const complete = isItemComplete(item)
            const pending = toggleMutation.isPending && toggleMutation.variables?.item.id === item.id

            return (
              <li
                key={item.id}
                className="flex items-start gap-3 rounded-lg border border-border/60 bg-card/80 px-3 py-2"
              >
                {readOnly ? (
                  complete ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                  ) : (
                    <Circle className="mt-0.5 h-4 w-4 text-muted-foreground" />
                  )
                ) : (
                  <Checkbox
                    checked={complete}
                    disabled={pending}
                    onCheckedChange={(checked) => {
                      toggleMutation.mutate({ item, checked: checked === true })
                    }}
                    className="mt-0.5"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p className={`text-sm ${complete ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                    {item.title}
                    {item.is_required ? (
                      <span className="ml-2 text-xs font-medium text-amber-700">Required</span>
                    ) : (
                      <span className="ml-2 text-xs text-muted-foreground">Optional</span>
                    )}
                    {item.requires_signature ? (
                      <span className="ml-2 text-xs font-medium text-purple-700">
                        Signature required
                      </span>
                    ) : null}
                  </p>
                  {item.description ? (
                    <p className="mt-1 text-xs text-muted-foreground">{item.description}</p>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
